/** The panel's readout: the moment the time pill names and each series' reading there. */
import { drawnTimeMs } from "../build-time-panel-options";
import { findNearestReading, lastReading, type SeriesReading } from "../hover-values/value-at-time";
import { type PreparedSeries, staleAfterMsFor } from "../prepare-series";

/**
 * stale: a live range where every reporting series is past its stale limit.
 * past: a fixed range reads nothing until hovered; the value at a chosen window's end is not a number anyone wants.
 */
export type ReadoutMode = "live" | "stale" | "cursor" | "past";

export type ReadoutRow = {
    series: PreparedSeries;
    /** In the axis unit, like the dots. null prints as "—". */
    value: number | null;
    /** When that reading was taken. A bar's is its bucket's start; the bar is drawn centred on the bucket. */
    timeMs: number | null;
    /** How long ago the stream last reported, set only once that is past its category's stale limit. Live and stale modes only. */
    staleForMs: number | null;
};

export type Readout = {
    mode: ReadoutMode;
    /** Where the line sits: the newest reading at rest, the nearest point while hovering. null in past mode, or when a live panel has no readings at all; the pill hides. */
    timeMs: number | null;
    rows: ReadoutRow[];
};

const emptyRow = (series: PreparedSeries): ReadoutRow => ({ series, value: null, timeMs: null, staleForMs: null });

type NearestReading = { entry: PreparedSeries; reading: SeriesReading | null };

function nearestReadings(series: PreparedSeries[], timeMs: number): NearestReading[] {
    return series.map((entry) => ({
        entry,
        reading: findNearestReading(entry.points, timeMs, entry, staleAfterMsFor(entry.axisUnitCategory)),
    }));
}

/** The drawn point nearest the pointer across the panel, which is also where echarts snaps the dashed line. */
function snappedTimeMs(readings: NearestReading[], hoveredTimeMs: number): number {
    const times = readings.flatMap(({ entry, reading }) => (reading ? [drawnTimeMs(entry, reading.timeMs)] : []));
    if (times.length === 0) return hoveredTimeMs;
    return times.reduce((nearest, time) =>
        Math.abs(time - hoveredTimeMs) < Math.abs(nearest - hoveredTimeMs) ? time : nearest,
    );
}

function newestTimeMs(rows: ReadoutRow[]): number | null {
    return rows.reduce<number | null>(
        (newest, row) => (row.timeMs !== null && (newest === null || row.timeMs > newest) ? row.timeMs : newest),
        null,
    );
}

export function buildReadout({
    series,
    hoveredTimeMs,
    snapToReading,
    isLiveRange,
    nowMs,
}: {
    series: PreparedSeries[];
    hoveredTimeMs: number | null;
    /** Off while the pointer is over the irrigation band: that axis has nothing to snap to, so the linked line stays at the pointer. */
    snapToReading: boolean;
    /** A relative preset, so the window ends now. */
    isLiveRange: boolean;
    nowMs: number;
}): Readout {
    if (hoveredTimeMs !== null) {
        // Every row reads at the pill's moment, not the raw pointer, so the rows describe the time the pill names.
        const snapped = snapToReading
            ? snappedTimeMs(nearestReadings(series, hoveredTimeMs), hoveredTimeMs)
            : hoveredTimeMs;
        const rows = nearestReadings(series, snapped).map(({ entry, reading }) =>
            reading
                ? { series: entry, value: reading.value, timeMs: reading.timeMs, staleForMs: null }
                : emptyRow(entry),
        );
        return { mode: "cursor", timeMs: snapped, rows };
    }

    if (!isLiveRange) return { mode: "past", timeMs: null, rows: series.map(emptyRow) };

    const rows = series.map((entry) => {
        const last = lastReading(entry.points);
        if (!last) return emptyRow(entry);
        const sinceMs = nowMs - last.timeMs;
        const isStale = sinceMs > staleAfterMsFor(entry.axisUnitCategory);
        return {
            series: entry,
            value: last.value,
            timeMs: last.timeMs,
            staleForMs: isStale ? sinceMs : null,
        };
    });
    const reporting = rows.filter((row) => row.value !== null);
    const allStale = reporting.length > 0 && reporting.every((row) => row.staleForMs !== null);
    return { mode: allStale ? "stale" : "live", timeMs: newestTimeMs(rows), rows };
}

/** The rows that get a dot: a current reading, and only the focused stream's while one is focused. */
export function dottedRows(readout: Readout, focusedStreamId?: number): ReadoutRow[] {
    return readout.rows.filter(
        (row) =>
            row.value !== null &&
            row.staleForMs === null &&
            (focusedStreamId === undefined || row.series.streamId === focusedStreamId),
    );
}

/**
 * Where a row's dot sits: at its reading as drawn, which for a bar is the bar's centre. A parked bar's
 * dot joins the parked line instead when the line crosses its bucket, so an open bucket reads at the line.
 */
export function dotTimeMs(readout: Readout, row: ReadoutRow): number | null {
    const { series } = row;
    if (row.timeMs === null) return null;
    const drawn = drawnTimeMs(series, row.timeMs);
    if (readout.mode === "cursor" || readout.timeMs === null || series.seriesType !== "bar") return drawn;
    const crossesBucket = readout.timeMs >= row.timeMs && readout.timeMs < row.timeMs + series.bucketMs;
    return crossesBucket ? readout.timeMs : drawn;
}
